import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Error severity levels for classification
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

// Error categories for classification
export type ErrorCategory = 
  | 'network'
  | 'network_timeout'
  | 'network_offline'
  | 'network_slow'
  | 'network_dns'
  | 'authentication'
  | 'authorization'
  | 'file_not_found'
  | 'server_error'
  | 'rate_limit'
  | 'download_limit'
  | 'payment_required'
  | 'payment_declined'
  | 'payment_insufficient_funds'
  | 'payment_processing_error'
  | 'payment_method_invalid'
  | 'payment_authentication_failed'
  | 'payment_timeout'
  | 'already_owned'
  | 'unknown';

// Download error interface
export interface DownloadError {
  id: string;
  userId?: string;
  sequenceId?: string;
  sequenceTitle?: string;
  errorCode: string;
  errorMessage: string;
  errorStack?: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  userAgent?: string;
  ipAddress?: string;
  timestamp: Date;
  context?: Record<string, any>;
}

// User recovery action interface
export interface RecoveryAction {
  type: 'retry' | 'contact_support' | 'check_payment' | 'check_network' | 'wait' | 'view_library' | 'run_diagnostics' | 'check_dns' | 'retry_with_resumption' | 'update_payment_method' | 'verify_payment_info' | 'try_different_card' | 'contact_bank';
  label: string;
  description: string;
  action?: () => void;
  url?: string;
}

// Admin notification interface
export interface AdminNotification {
  type: 'download_failure' | 'critical_error' | 'pattern_detected';
  severity: ErrorSeverity;
  title: string;
  message: string;
  errorDetails: DownloadError;
  metadata?: Record<string, any>;
}

/**
 * Classify error based on error message and context
 */
export function classifyDownloadError(
  error: Error | string,
  context?: Record<string, any>
): { category: ErrorCategory; severity: ErrorSeverity } {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorMessageLower = errorMessage.toLowerCase();

  // Network errors - specific types
  if (errorMessageLower.includes('timeout') ||
      errorMessageLower.includes('timed out') ||
      errorMessageLower.includes('request timeout')) {
    return { category: 'network_timeout', severity: 'medium' };
  }

  if (errorMessageLower.includes('offline') ||
      errorMessageLower.includes('no internet') ||
      errorMessageLower.includes('not connected')) {
    return { category: 'network_offline', severity: 'high' };
  }

  if (errorMessageLower.includes('dns') ||
      errorMessageLower.includes('name resolution') ||
      errorMessageLower.includes('getaddrinfo')) {
    return { category: 'network_dns', severity: 'medium' };
  }

  if (errorMessageLower.includes('slow') ||
      (context?.connectionQuality === 'poor') ||
      (context?.downloadSpeed && context.downloadSpeed < 50000)) { // < 50KB/s
    return { category: 'network_slow', severity: 'low' };
  }

  // General network errors
  if (errorMessageLower.includes('network') || 
      errorMessageLower.includes('connection') ||
      errorMessageLower.includes('fetch')) {
    return { category: 'network', severity: 'medium' };
  }

  // Authentication errors
  if (errorMessageLower.includes('unauthorized') ||
      errorMessageLower.includes('authentication') ||
      errorMessageLower.includes('invalid token')) {
    return { category: 'authentication', severity: 'medium' };
  }

  // Authorization errors
  if (errorMessageLower.includes('forbidden') ||
      errorMessageLower.includes('access denied') ||
      errorMessageLower.includes('permission')) {
    return { category: 'authorization', severity: 'high' };
  }

  // File not found
  if (errorMessageLower.includes('not found') ||
      errorMessageLower.includes('404') ||
      errorMessageLower.includes('file does not exist')) {
    return { category: 'file_not_found', severity: 'high' };
  }

  // Download limits
  if (errorMessageLower.includes('download limit') ||
      errorMessageLower.includes('limit exceeded') ||
      errorMessageLower.includes('too many downloads')) {
    return { category: 'download_limit', severity: 'low' };
  }

  // Already owned checks
  if ((typeof error === 'object' && error.name === 'AlreadyOwnedError') ||
      errorMessageLower.includes('already own') ||
      errorMessageLower.includes('already purchased') ||
      errorMessageLower.includes('are the creator') ||
      errorMessageLower.includes('check your library')) {
    return { category: 'already_owned', severity: 'low' };
  }

  // Payment-specific errors
  if (errorMessageLower.includes('card declined') ||
      errorMessageLower.includes('declined') ||
      errorMessageLower.includes('payment declined')) {
    return { category: 'payment_declined', severity: 'high' };
  }

  if (errorMessageLower.includes('insufficient funds') ||
      errorMessageLower.includes('insufficient balance') ||
      errorMessageLower.includes('not enough money')) {
    return { category: 'payment_insufficient_funds', severity: 'high' };
  }

  if (errorMessageLower.includes('payment method') ||
      errorMessageLower.includes('invalid card') ||
      errorMessageLower.includes('expired card') ||
      errorMessageLower.includes('invalid payment')) {
    return { category: 'payment_method_invalid', severity: 'medium' };
  }

  if (errorMessageLower.includes('3d secure') ||
      errorMessageLower.includes('authentication failed') ||
      errorMessageLower.includes('verification failed')) {
    return { category: 'payment_authentication_failed', severity: 'medium' };
  }

  if (errorMessageLower.includes('payment timeout') ||
      (errorMessageLower.includes('payment') && errorMessageLower.includes('timeout'))) {
    return { category: 'payment_timeout', severity: 'medium' };
  }

  if (errorMessageLower.includes('payment processing') ||
      errorMessageLower.includes('stripe error') ||
      errorMessageLower.includes('gateway error')) {
    return { category: 'payment_processing_error', severity: 'high' };
  }

  // General payment required
  if (errorMessageLower.includes('payment') ||
      errorMessageLower.includes('purchase') ||
      errorMessageLower.includes('not owned')) {
    return { category: 'payment_required', severity: 'medium' };
  }

  // Rate limiting
  if (errorMessageLower.includes('rate limit') ||
      errorMessageLower.includes('too many requests') ||
      errorMessageLower.includes('throttled')) {
    return { category: 'rate_limit', severity: 'low' };
  }

  // Server errors
  if (errorMessageLower.includes('500') ||
      errorMessageLower.includes('internal server') ||
      errorMessageLower.includes('server error')) {
    return { category: 'server_error', severity: 'critical' };
  }

  // Default to unknown with medium severity
  return { category: 'unknown', severity: 'medium' };
}

/**
 * Generate user-friendly error message and recovery actions
 */
export function generateUserErrorResponse(
  error: DownloadError
): { message: string; description: string; actions: RecoveryAction[] } {
  const { category, severity } = error;

  switch (category) {
    case 'network':
      return {
        message: 'Network Connection Issue',
        description: 'Unable to connect to our download servers. Please check your internet connection.',
        actions: [
          {
            type: 'check_network',
            label: 'Check Connection',
            description: 'Test your internet connection',
            url: 'https://www.google.com'
          },
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the download after checking your connection'
          }
        ]
      };

    case 'network_timeout':
      return {
        message: 'Connection Timeout',
        description: 'The download request timed out. This may be due to a slow connection or server issues.',
        actions: [
          {
            type: 'retry_with_resumption',
            label: 'Resume Download',
            description: 'Try resuming the download from where it left off'
          },
          {
            type: 'run_diagnostics',
            label: 'Test Connection',
            description: 'Run network diagnostics to identify the issue'
          },
          {
            type: 'wait',
            label: 'Wait & Retry',
            description: 'Wait 30 seconds and try again with longer timeout'
          }
        ]
      };

    case 'network_offline':
      return {
        message: 'No Internet Connection',
        description: 'Your device appears to be offline. Please check your internet connection.',
        actions: [
          {
            type: 'check_network',
            label: 'Check Connection',
            description: 'Test your internet connection',
            url: 'https://www.google.com'
          },
          {
            type: 'wait',
            label: 'Retry When Online',
            description: 'Download will automatically retry when connection is restored'
          }
        ]
      };

    case 'network_dns':
      return {
        message: 'DNS Resolution Failed',
        description: 'Unable to resolve the server address. This may be a DNS or firewall issue.',
        actions: [
          {
            type: 'check_dns',
            label: 'Check DNS',
            description: 'Test DNS resolution',
            url: 'https://dns.google/resolve?name=google.com&type=A'
          },
          {
            type: 'run_diagnostics',
            label: 'Run Diagnostics',
            description: 'Check network and DNS connectivity'
          },
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the download'
          }
        ]
      };

    case 'network_slow':
      return {
        message: 'Slow Connection Detected',
        description: 'Your connection is slower than usual. Download may take longer than expected.',
        actions: [
          {
            type: 'retry_with_resumption',
            label: 'Continue Download',
            description: 'Continue with optimized settings for slow connections'
          },
          {
            type: 'run_diagnostics',
            label: 'Test Speed',
            description: 'Check your connection speed'
          },
          {
            type: 'wait',
            label: 'Try Later',
            description: 'Try downloading when you have a better connection'
          }
        ]
      };

    case 'authentication':
      return {
        message: 'Authentication Required',
        description: 'Please sign in to continue with your download.',
        actions: [
          {
            type: 'retry',
            label: 'Sign In',
            description: 'Sign in to your account and try again'
          }
        ]
      };

    case 'authorization':
      return {
        message: 'Access Denied',
        description: 'You don\'t have permission to download this sequence. Please check if you own it.',
        actions: [
          {
            type: 'check_payment',
            label: 'Check Purchase',
            description: 'Verify your purchase history'
          },
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Get help from our support team'
          }
        ]
      };

    case 'file_not_found':
      return {
        message: 'File Not Available',
        description: 'The sequence file could not be found. This may be a temporary issue.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'The file may become available shortly'
          },
          {
            type: 'contact_support',
            label: 'Report Issue',
            description: 'Let us know about this problem'
          }
        ]
      };

    case 'download_limit':
      return {
        message: 'Download Limit Reached',
        description: 'You have reached the download limit for this sequence.',
        actions: [
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Request additional downloads if needed'
          }
        ]
      };

    case 'already_owned':
      return {
        message: 'Already in Your Library',
        description: 'You already have access to this sequence. Check your library to re-download it.',
        actions: [
          {
            type: 'check_payment',
            label: 'View Library',
            description: 'Go to your library to access this sequence'
          },
          {
            type: 'retry',
            label: 'Re-download',
            description: 'Download this sequence again from your library'
          }
        ]
      };

    case 'payment_required':
      return {
        message: 'Purchase Required',
        description: 'This sequence requires a purchase to download.',
        actions: [
          {
            type: 'check_payment',
            label: 'Purchase Sequence',
            description: 'Complete your purchase to download'
          }
        ]
      };

    case 'payment_declined':
      return {
        message: 'Payment Declined',
        description: 'Your payment was declined by your bank or card issuer. This could be due to security restrictions or account settings.',
        actions: [
          {
            type: 'try_different_card',
            label: 'Try Different Card',
            description: 'Use a different payment method'
          },
          {
            type: 'contact_bank',
            label: 'Contact Your Bank',
            description: 'Check with your bank about the declined transaction'
          },
          {
            type: 'verify_payment_info',
            label: 'Verify Card Details',
            description: 'Ensure your card information is correct'
          }
        ]
      };

    case 'payment_insufficient_funds':
      return {
        message: 'Insufficient Funds',
        description: 'Your payment method does not have sufficient funds to complete this purchase.',
        actions: [
          {
            type: 'update_payment_method',
            label: 'Add Funds or Use Different Card',
            description: 'Add money to your account or try a different payment method'
          },
          {
            type: 'contact_bank',
            label: 'Check Account Balance',
            description: 'Verify your account balance with your bank'
          }
        ]
      };

    case 'payment_method_invalid':
      return {
        message: 'Invalid Payment Method',
        description: 'There\'s an issue with your payment method. It may be expired, invalid, or not supported.',
        actions: [
          {
            type: 'verify_payment_info',
            label: 'Update Payment Info',
            description: 'Check and update your card details'
          },
          {
            type: 'try_different_card',
            label: 'Try Different Card',
            description: 'Use a different payment method'
          }
        ]
      };

    case 'payment_authentication_failed':
      return {
        message: 'Payment Authentication Failed',
        description: 'Your bank requires additional verification for this transaction. This is usually due to 3D Secure or similar security measures.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry payment and complete the authentication process'
          },
          {
            type: 'contact_bank',
            label: 'Contact Your Bank',
            description: 'Check your authentication settings with your bank'
          }
        ]
      };

    case 'payment_timeout':
      return {
        message: 'Payment Timeout',
        description: 'The payment process took too long and timed out. This may be due to network issues or server delays.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the payment process'
          },
          {
            type: 'check_network',
            label: 'Check Connection',
            description: 'Ensure you have a stable internet connection'
          }
        ]
      };

    case 'payment_processing_error':
      return {
        message: 'Payment Processing Error',
        description: 'There was an error processing your payment. This is usually a temporary issue with our payment system.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the payment in a few minutes'
          },
          {
            type: 'try_different_card',
            label: 'Try Different Card',
            description: 'Use an alternative payment method'
          },
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Get help if the problem persists'
          }
        ]
      };

    case 'rate_limit':
      return {
        message: 'Too Many Requests',
        description: 'Please wait a moment before trying again.',
        actions: [
          {
            type: 'wait',
            label: 'Wait & Retry',
            description: 'Wait 30 seconds and try again'
          }
        ]
      };

    case 'server_error':
      return {
        message: 'Server Error',
        description: 'Our servers are experiencing issues. Our team has been notified.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'The issue may be resolved shortly'
          },
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Get updates on this issue'
          }
        ]
      };

    default:
      return {
        message: 'Download Failed',
        description: 'An unexpected error occurred while preparing your download.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the download'
          },
          {
            type: 'contact_support',
            label: 'Get Help',
            description: 'Contact support if the problem persists'
          }
        ]
      };
  }
}

/**
 * Log download error to database for tracking and analytics
 */
export async function logDownloadError(
  errorData: Omit<DownloadError, 'id' | 'timestamp'>
): Promise<DownloadError> {
  const error: DownloadError = {
    ...errorData,
    id: crypto.randomUUID(),
    timestamp: new Date(),
  };

  try {
    const { error: dbError } = await supabase
      .from('download_error_logs')
      .insert({
        id: error.id,
        user_id: error.userId,
        sequence_id: error.sequenceId,
        sequence_title: error.sequenceTitle,
        error_code: error.errorCode,
        error_message: error.errorMessage,
        error_stack: error.errorStack,
        category: error.category,
        severity: error.severity,
        user_agent: error.userAgent,
        ip_address: error.ipAddress,
        context: error.context,
        created_at: error.timestamp.toISOString(),
      });

    if (dbError) {
      console.error('Failed to log download error to database:', dbError);
    }
  } catch (err) {
    console.error('Error logging download error:', err);
  }

  return error;
}

/**
 * Log admin notification for tracking (email sending will be implemented later)
 */
export async function logAdminNotification(
  notification: AdminNotification
): Promise<boolean> {
  try {
    // Only log notifications for medium severity and above
    if (notification.severity === 'low') {
      return true;
    }

    // Check if we should throttle notifications for this error type
    const shouldThrottle = await shouldThrottleNotification(notification);
    if (shouldThrottle) {
      console.log('Throttling admin notification to prevent spam');
      return true;
    }

    // For now, just log to console and database
    console.warn(`Admin notification would be sent: ${notification.title}`, {
      severity: notification.severity,
      category: notification.errorDetails.category,
      errorId: notification.errorDetails.id,
      message: notification.message,
    });

    // Log to database for tracking
    await supabase
      .from('admin_notification_logs')
      .insert({
        type: notification.type,
        severity: notification.severity,
        title: notification.title,
        error_category: notification.errorDetails.category,
        error_id: notification.errorDetails.id,
        recipient: 'lighthunt@towb.in',
        created_at: new Date().toISOString(),
      });

    return true;

  } catch (error) {
    console.error('Error logging admin notification:', error);
    return false;
  }
}

/**
 * Check if we should throttle notifications to prevent spam
 */
async function shouldThrottleNotification(
  notification: AdminNotification
): Promise<boolean> {
  try {
    const timeWindow = 300000; // 5 minutes in milliseconds
    const maxNotifications = 5; // Max notifications per time window

    const { data, error } = await supabase
      .from('admin_notification_logs')
      .select('id')
      .eq('type', notification.type)
      .eq('error_category', notification.errorDetails.category)
      .gte('created_at', new Date(Date.now() - timeWindow).toISOString())
      .order('created_at', { ascending: false })
      .limit(maxNotifications);

    if (error) {
      console.error('Error checking notification throttle:', error);
      return false; // Don't throttle if we can't check
    }

    return (data?.length || 0) >= maxNotifications;

  } catch (error) {
    console.error('Error in throttle check:', error);
    return false;
  }
}



/**
 * Main function to handle download errors with comprehensive notification
 */
export async function handleDownloadError(
  error: Error | string,
  context: {
    userId?: string;
    sequenceId?: string;
    sequenceTitle?: string;
    userAgent?: string;
    ipAddress?: string;
    additionalContext?: Record<string, any>;
  } = {}
): Promise<{
  userResponse: ReturnType<typeof generateUserErrorResponse>;
  errorId: string;
}> {
  try {
    // Classify the error
    const { category, severity } = classifyDownloadError(error, context.additionalContext);

    // Create error data
    const errorData: Omit<DownloadError, 'id' | 'timestamp'> = {
      userId: context.userId,
      sequenceId: context.sequenceId,
      sequenceTitle: context.sequenceTitle,
      errorCode: typeof error === 'string' ? 'UNKNOWN' : error.name,
      errorMessage: typeof error === 'string' ? error : error.message,
      errorStack: typeof error === 'string' ? undefined : error.stack,
      category,
      severity,
      userAgent: context.userAgent,
      ipAddress: context.ipAddress,
      context: context.additionalContext,
    };

    // Log the error
    const loggedError = await logDownloadError(errorData);

    // Generate user-friendly response
    const userResponse = generateUserErrorResponse(loggedError);

    // Send admin notification if needed
    if (severity === 'high' || severity === 'critical') {
      const adminNotification: AdminNotification = {
        type: 'download_failure',
        severity,
        title: `Download Failure: ${category}`,
        message: `Download failed for sequence "${context.sequenceTitle || 'Unknown'}" with error: ${errorData.errorMessage}`,
        errorDetails: loggedError,
        metadata: {
          userAgent: context.userAgent,
          ipAddress: context.ipAddress,
          timestamp: loggedError.timestamp.toISOString(),
        }
      };

      await logAdminNotification(adminNotification);
    }

    return {
      userResponse,
      errorId: loggedError.id,
    };

  } catch (handlingError) {
    console.error('Error in handleDownloadError:', handlingError);
    
    // Fallback response
    return {
      userResponse: {
        message: 'Download Failed',
        description: 'An unexpected error occurred. Please try again or contact support.',
        actions: [
          {
            type: 'retry',
            label: 'Try Again',
            description: 'Retry the download'
          },
          {
            type: 'contact_support',
            label: 'Contact Support',
            description: 'Get help with this issue'
          }
        ]
      },
      errorId: crypto.randomUUID(),
    };
  }
}

/**
 * Show user-friendly error notification using toast
 */
export function showDownloadErrorToast(
  userResponse: ReturnType<typeof generateUserErrorResponse>,
  errorId: string
): void {
  const primaryAction = userResponse.actions[0];
  
  toast.error(userResponse.message, {
    description: userResponse.description,
    duration: 8000,
    action: primaryAction ? {
      label: primaryAction.label,
      onClick: () => {
        if (primaryAction.url) {
          window.open(primaryAction.url, '_blank');
        } else if (primaryAction.action) {
          primaryAction.action();
        }
      }
    } : undefined,
    id: errorId, // Prevent duplicate toasts
  });
}

/**
 * Get error analytics for admin dashboard
 */
export async function getDownloadErrorAnalytics(
  timeRange: { start: Date; end: Date }
): Promise<{
  totalErrors: number;
  errorsByCategory: Record<ErrorCategory, number>;
  errorsBySeverity: Record<ErrorSeverity, number>;
  topErrors: Array<{ message: string; count: number }>;
  affectedUsers: number;
  affectedSequences: number;
}> {
  try {
    const { data: errors, error } = await supabase
      .from('download_error_logs')
      .select('*')
      .gte('created_at', timeRange.start.toISOString())
      .lte('created_at', timeRange.end.toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;

    const totalErrors = errors?.length || 0;
    
    // Count by category
    const errorsByCategory = errors?.reduce((acc, err) => {
      acc[err.category] = (acc[err.category] || 0) + 1;
      return acc;
    }, {} as Record<ErrorCategory, number>) || {};

    // Count by severity
    const errorsBySeverity = errors?.reduce((acc, err) => {
      acc[err.severity] = (acc[err.severity] || 0) + 1;
      return acc;
    }, {} as Record<ErrorSeverity, number>) || {};

    // Top error messages
    const errorMessages = errors?.reduce((acc, err) => {
      acc[err.error_message] = (acc[err.error_message] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};

    const topErrors = Object.entries(errorMessages)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([message, count]) => ({ message, count }));

    // Unique counts
    const affectedUsers = new Set(errors?.map(err => err.user_id).filter(Boolean)).size;
    const affectedSequences = new Set(errors?.map(err => err.sequence_id).filter(Boolean)).size;

    return {
      totalErrors,
      errorsByCategory,
      errorsBySeverity,
      topErrors,
      affectedUsers,
      affectedSequences,
    };

  } catch (error) {
    console.error('Error getting download error analytics:', error);
    throw error;
  }
} 